import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Button,
  Input,
  Field,
  Dropdown,
  Option,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { PersonAddRegular, CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { DATA_LOCATIONS } from '../config/constants';

const LANGUAGE_OPTIONS = [
  { key: 'en-US', text: 'English (US)' },
  { key: 'en-GB', text: 'English (UK)' },
  { key: 'es-ES', text: 'Spanish' },
  { key: 'fr-FR', text: 'French' },
  { key: 'de-DE', text: 'German' },
  { key: 'pt-BR', text: 'Portuguese (Brazil)' },
  { key: 'pl-PL', text: 'Polish' },
];

const USAGE_LOCATIONS = [
  { key: 'US', text: 'United States (US)' },
  { key: 'MX', text: 'Mexico (MX)' },
  { key: 'CA', text: 'Canada (CA)' },
  { key: 'GB', text: 'United Kingdom (GB)' },
  { key: 'DE', text: 'Germany (DE)' },
  { key: 'FR', text: 'France (FR)' },
  { key: 'ES', text: 'Spain (ES)' },
  { key: 'PL', text: 'Poland (PL)' },
  { key: 'BR', text: 'Brazil (BR)' },
];

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXXL,
    maxWidth: '800px',
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  card: {
    padding: tokens.spacingHorizontalXL,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
  },
  credentialsBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalM,
  },
  credentialRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  credentialLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  credentialValue: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    marginRight: tokens.spacingHorizontalS,
  },
  actions: {
    marginTop: tokens.spacingVerticalL,
    display: 'flex',
    gap: tokens.spacingHorizontalM,
  },
});

export function QuickCreatePage() {
  const styles = useStyles();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    preferredLanguage: 'en-US',
    preferredDataLocation: 'EUR',
    usageLocation: 'US',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{
    userPrincipalName: string;
    temporaryPassword: string;
    displayName: string;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const response = await apiClient.post<{
        success: boolean;
        data: {
          userPrincipalName: string;
          temporaryPassword: string;
          displayName: string;
        };
      }>('/quick-create-user', form);

      setCreatedCredentials(response.data);
      setDialogOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCreatedCredentials(null);
    setForm({
      firstName: '',
      lastName: '',
      displayName: '',
      preferredLanguage: 'en-US',
      preferredDataLocation: 'EUR',
      usageLocation: 'US',
    });
  };

  const isFormValid = form.firstName && form.lastName && form.displayName;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Quick Create User</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS }}>
          Create a new user account instantly without approval workflow.
        </Body1>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Card className={styles.card}>
        <div className={styles.form}>
          <div className={styles.formRow}>
            <Field label="First Name" required>
              <Input
                value={form.firstName}
                onChange={(_, d) => setForm({ ...form, firstName: d.value })}
                placeholder="John"
              />
            </Field>
            <Field label="Last Name" required>
              <Input
                value={form.lastName}
                onChange={(_, d) => setForm({ ...form, lastName: d.value })}
                placeholder="Doe"
              />
            </Field>
          </div>

          <Field label="Display Name" required>
            <Input
              value={form.displayName}
              onChange={(_, d) => setForm({ ...form, displayName: d.value })}
              placeholder="John Doe"
            />
          </Field>

          <div className={styles.formRow}>
            <Field label="Preferred Language" required>
              <Dropdown
                value={LANGUAGE_OPTIONS.find(l => l.key === form.preferredLanguage)?.text || ''}
                onOptionSelect={(_, d) =>
                  setForm({ ...form, preferredLanguage: d.optionValue as string })
                }
              >
                {LANGUAGE_OPTIONS.map((lang) => (
                  <Option key={lang.key} value={lang.key}>{lang.text}</Option>
                ))}
              </Dropdown>
            </Field>
            <Field label="Data Location" required>
              <Dropdown
                value={DATA_LOCATIONS.find(l => l.key === form.preferredDataLocation)?.text || ''}
                onOptionSelect={(_, d) =>
                  setForm({ ...form, preferredDataLocation: d.optionValue as string })
                }
              >
                {DATA_LOCATIONS.map((loc) => (
                  <Option key={loc.key} value={loc.key}>{loc.text}</Option>
                ))}
              </Dropdown>
            </Field>
          </div>

          <Field label="Usage Location" required>
            <Dropdown
              value={USAGE_LOCATIONS.find(l => l.key === form.usageLocation)?.text || ''}
              onOptionSelect={(_, d) =>
                setForm({ ...form, usageLocation: d.optionValue as string })
              }
            >
              {USAGE_LOCATIONS.map((loc) => (
                <Option key={loc.key} value={loc.key}>{loc.text}</Option>
              ))}
            </Dropdown>
          </Field>

          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={creating ? <Spinner size="tiny" /> : <PersonAddRegular />}
              onClick={handleCreate}
              disabled={creating || !isFormValid}
            >
              {creating ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Success Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(_, data) => data.open || handleClose()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              <CheckmarkRegular className={styles.successIcon} />
              User Created Successfully
            </DialogTitle>
            <DialogContent>
              {createdCredentials && (
                <div>
                  <Body1 style={{ marginBottom: tokens.spacingVerticalM }}>
                    Account for <strong>{createdCredentials.displayName}</strong> has been created.
                  </Body1>
                  <div className={styles.credentialsBox}>
                    <div className={styles.credentialRow}>
                      <div>
                        <div className={styles.credentialLabel}>Username</div>
                        <div className={styles.credentialValue}>{createdCredentials.userPrincipalName}</div>
                      </div>
                      <Button
                        appearance="subtle"
                        icon={copiedField === 'upn' ? <CheckmarkRegular /> : <CopyRegular />}
                        onClick={() => handleCopy(createdCredentials.userPrincipalName, 'upn')}
                      >
                        {copiedField === 'upn' ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    <div className={styles.credentialRow}>
                      <div>
                        <div className={styles.credentialLabel}>Temporary Password</div>
                        <div className={styles.credentialValue}>{createdCredentials.temporaryPassword}</div>
                      </div>
                      <Button
                        appearance="subtle"
                        icon={copiedField === 'password' ? <CheckmarkRegular /> : <CopyRegular />}
                        onClick={() => handleCopy(createdCredentials.temporaryPassword, 'password')}
                      >
                        {copiedField === 'password' ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <Body1 style={{ marginTop: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3 }}>
                    Please share these credentials securely with the new user.
                  </Body1>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleClose}>
                Done
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
